// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle } from "lucide-react";
import MultiSelectFilter from "./MultiSelectFilter";

/**
 * Modal for bulk editing multiple product fields.
 * Only fields that the user fills are included in the payload (partial update).
 */
const BulkEditModal = ({
  isOpen,
  onClose,
  onApply,
  selectedCount,
  filterOptions,
}) => {
  const [formData, setFormData] = useState({
    vhp_archiving_flag: "",
    vhp_location: "",
    type: "",
    vhp_product_type: "",
    bruttopreis_pro_einheit: "",
    bookable_for_scenarios: [],
    layout_position: "",
    paket: [],
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFormData({
        vhp_archiving_flag: "",
        vhp_location: "",
        type: "",
        vhp_product_type: "",
        bruttopreis_pro_einheit: "",
        bookable_for_scenarios: [],
        layout_position: "",
        paket: [],
      });
      setErrors({});
      setOpenDropdown(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const hasAnyValue = () => {
    const price = formData.bruttopreis_pro_einheit;
    return (
      formData.vhp_archiving_flag !== "" ||
      formData.vhp_location !== "" ||
      formData.type !== "" ||
      formData.vhp_product_type !== "" ||
      (price !== "" && !Number.isNaN(parseFloat(price))) ||
      (Array.isArray(formData.bookable_for_scenarios) &&
        formData.bookable_for_scenarios.length > 0) ||
      formData.layout_position !== "" ||
      (Array.isArray(formData.paket) && formData.paket.length > 0)
    );
  };

  const buildPayload = () => {
    const payload = {};
    if (formData.vhp_archiving_flag !== "") {
      payload.vhp_archiving_flag = formData.vhp_archiving_flag;
    }
    if (formData.vhp_location !== "") {
      const loc = formData.vhp_location;
      payload.vhp_location =
        typeof loc === "number" ? loc.toString() : String(loc);
    }
    if (formData.type !== "") {
      payload.type = formData.type;
    }
    if (formData.vhp_product_type !== "") {
      payload.vhp_product_type = formData.vhp_product_type;
    }
    const price = parseFloat(formData.bruttopreis_pro_einheit);
    if (
      formData.bruttopreis_pro_einheit !== "" &&
      !Number.isNaN(price) &&
      price >= 0
    ) {
      payload.bruttopreis_pro_einheit = price;
    }
    if (
      Array.isArray(formData.bookable_for_scenarios) &&
      formData.bookable_for_scenarios.length > 0
    ) {
      payload.bookable_for_scenarios = formData.bookable_for_scenarios.map(
        (s) =>
          typeof s === "object" && s !== null
            ? s.scenario_code || s.name || s
            : s,
      );
    }
    if (formData.layout_position !== "") {
      payload.layout_position = formData.layout_position;
    }
    // User changed package: "none" or empty selection → []; otherwise S/M/L
    if (Array.isArray(formData.paket) && formData.paket.length > 0) {
      payload.paket = formData.paket.includes("none")
        ? []
        : formData.paket.filter((p) => p !== "none");
    }
    return payload;
  };

  const handleSubmit = () => {
    if (!hasAnyValue()) {
      setErrors({
        form: "Bitte wählen Sie mindestens ein Feld zum Aktualisieren aus.",
      });
      return;
    }
    const price = formData.bruttopreis_pro_einheit;
    if (
      price !== "" &&
      (Number.isNaN(parseFloat(price)) || parseFloat(price) < 0)
    ) {
      setErrors({ bruttopreis_pro_einheit: "Ungültiger Preis" });
      return;
    }
    setErrors({});
    onApply(buildPayload());
    onClose();
  };

  const scenarioOptions = (filterOptions?.scenarios || []).map((s) => ({
    value: s.value ?? s.scenario_code ?? s.name,
    label: s.label ?? s.name ?? s.scenario_code ?? "—",
  }));
  const locationOptions = (filterOptions?.location || []).map((l) => ({
    value: l.value ?? l.id,
    label: l.label ?? l.name ?? "—",
  }));
  const statusOptions = (filterOptions?.status || []).map((s) => ({
    value: s.value ?? s.id,
    label: s.label ?? s.name ?? "—",
  }));
  const typeOptions = (filterOptions?.type || []).map((t) => ({
    value: t.value ?? t.id,
    label: t.label ?? t.name ?? "—",
  }));
  const productTypeOptions = (filterOptions?.product_type || []).map((t) => ({
    value: t.value ?? t.id,
    label: t.label ?? t.name ?? "—",
  }));
  const layoutOptions = (filterOptions?.layout_positions || []).map((l) => ({
    value: l.value ?? l,
    label: l.label ?? l ?? "—",
  }));
  // Exclude "none" from API if present, add single "None (no package)" (API receives [])
  const apiPaket = filterOptions?.paket_options || [];
  const paketOptions = [
    ...apiPaket
      .filter((p) =>
        typeof p === "object" ? p?.value !== "none" : p !== "none",
      )
      .map((p) => ({
        value: typeof p === "object" ? (p?.value ?? p) : p,
        label: typeof p === "object" ? (p?.label ?? p) : (p ?? "—"),
      })),
    { value: "none", label: "None (no package)" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Bulk bearbeiten ({selectedCount} Produkte)
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-4">
            Nur ausgefüllte Felder werden für alle ausgewählten Produkte
            übernommen. Leere Felder bleiben unverändert.
          </p>
          {errors.form && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.form}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.vhp_archiving_flag}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    vhp_archiving_flag: e.target.value,
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">— Nicht ändern</option>
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Standort
              </label>
              <select
                value={formData.vhp_location}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    vhp_location: val === "" ? "" : val,
                  }));
                }}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">— Nicht ändern</option>
                {locationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategorie (Typ)
              </label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, type: e.target.value }))
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">— Nicht ändern</option>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Produkttyp
              </label>
              <select
                value={formData.vhp_product_type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    vhp_product_type: e.target.value,
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">— Nicht ändern</option>
                {productTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bruttopreis (€)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="— Nicht ändern"
                value={formData.bruttopreis_pro_einheit}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    bruttopreis_pro_einheit: e.target.value,
                  }))
                }
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.bruttopreis_pro_einheit
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
              />
              {errors.bruttopreis_pro_einheit && (
                <p className="text-red-600 text-xs mt-1">
                  {errors.bruttopreis_pro_einheit}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Anzeigebereich (Layout)
              </label>
              <select
                value={formData.layout_position}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    layout_position: e.target.value,
                  }))
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">— Nicht ändern</option>
                {layoutOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buchbar für (Szenarien)
              </label>
              <MultiSelectFilter
                label="Szenarien"
                options={scenarioOptions}
                selectedValues={formData.bookable_for_scenarios}
                onSelectionChange={(values) =>
                  setFormData((prev) => ({
                    ...prev,
                    bookable_for_scenarios: values,
                  }))
                }
                isOpen={openDropdown === "scenarios"}
                onToggle={(key) =>
                  setOpenDropdown((prev) => (prev === key ? null : key))
                }
                filterKey="scenarios"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paket (S, M, L)
              </label>
              <MultiSelectFilter
                label="Paket"
                options={paketOptions}
                selectedValues={
                  Array.isArray(formData.paket) && formData.paket.length === 0
                    ? ["none"]
                    : formData.paket || []
                }
                onSelectionChange={(values) =>
                  setFormData((prev) => ({ ...prev, paket: values }))
                }
                isOpen={openDropdown === "paket"}
                onToggle={(key) =>
                  setOpenDropdown((prev) => (prev === key ? null : key))
                }
                filterKey="paket"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <CheckCircle className="w-4 h-4" />
            Auf {selectedCount} Produkte anwenden
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditModal;
